import { Component, Input } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AddressDto,
    AddressService,
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { AddressDialoguesService } from '../address-dialogues.service';

@Component({
    selector: 'address-list',
    templateUrl: './address-list.component.html'
})
export class AddressList {
    @Input() organisationId: number;
    addresses: AddressDto[];
    @Input() canEdit: boolean = false;

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private addressService: AddressService,
        private commonDialoguesService: CommonDialoguesService,
        private addressDialoguesService: AddressDialoguesService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.refreshProfile();
        this.addressService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(change => this.refreshProfile());
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshProfile() {
        const doneWorking = this.workTracker.createTracker();
        this.addressService
            .findAddressesForOrganisation(this.organisationId)
            .toPromise()
            .then((addresses: AddressDto[]) => (this.addresses = addresses))
            .finally(() => doneWorking());
    }

    newAddress() {
        this.addressDialoguesService.showAddressEdit(
            this.organisationId,
            undefined,
            'New Address'
        );
    }

    deleteAddress(address: AddressDto) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete Address',
                'Do you wish to delete this address',
                'Delete',
                () =>
                    firstValueFrom(
                        this.addressService.deleteAddress(address.id).pipe(
                            tap({
                                error: (error: FlyFreelyError) =>
                                    this.logging.error(
                                        error,
                                        `Error deleting address: ${error.message}`
                                    )
                            })
                        )
                    )
            )
            .then(() => this.refreshProfile(), DO_NOTHING)
            .catch(() => doneWorking())
            .finally(() => doneWorking());
    }

    editAddress(address: AddressDto) {
        this.addressDialoguesService.showAddressEdit(
            this.organisationId,
            { ...address },
            'Update Address'
        );
    }
}
